/* A gallery-level introduction, independent of Swiper, video and booking.
   A new section gets a new element; an existing element never replays. */
(() => {
  if (customElements.get('laserlab-media-cue')) return;

  customElements.define('laserlab-media-cue', class extends HTMLElement {
    connectedCallback() {
      if (this._connectedOnce) return;
      this._connectedOnce = true;
      this._phase = 'waiting';
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this._gallery = this.closest('media-gallery');
      this._host = this.parentElement;
      this._viewer = this._host?.querySelector('.product__media-gallery-viewer');
      if (!this._gallery || !this._viewer) return;

      this._events = new AbortController();
      const signal = this._events.signal;
      const dismiss = () => this._dismiss(true);
      this._gallery.addEventListener('pointerdown', dismiss, { capture: true, passive: true, signal });
      this._gallery.addEventListener('keydown', event => {
        if (!['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) dismiss();
      }, { capture: true, signal });
      window.addEventListener('pagehide', dismiss, { signal });
      window.addEventListener('resize', () => this._syncFrame(), { passive: true, signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden && this._phase === 'visible') dismiss();
        else this._maybeShow();
      }, { signal });

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(() => this._syncFrame());
        this._resizeObserver.observe(this._host);
      }

      if ('IntersectionObserver' in window) {
        this._intersectionObserver = new IntersectionObserver(entries => {
          const entry = entries.find(item => item.target === this._frame);
          if (!entry) return;
          this._intersecting = entry.isIntersecting && entry.intersectionRatio >= .15;
          this._maybeShow();
        }, { threshold: [0, .15] });
      } else {
        window.addEventListener('scroll', () => this._scheduleMeasure(), { passive: true, signal });
      }

      // Swiper changes only slide classes; variants can also reorder the
      // desktop list. Neither should restart an introduction already shown.
      this._mutationObserver = new MutationObserver(mutations => {
        const changed = mutations.some(mutation =>
          mutation.type === 'attributes'
            ? mutation.target.classList.contains('product__media-item')
            : mutation.target.matches('.product__media-list')
        );
        if (changed) this._syncFrame();
      });
      this._mutationObserver.observe(this._viewer, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true,
      });

      this._syncFrame();
    }

    disconnectedCallback() {
      this._dismiss(true);
    }

    _getFrame() {
      const desktopGrid = window.matchMedia('(min-width: 768px)').matches &&
        this._gallery.dataset.enableDesktopSlider !== 'true';
      const item = (!desktopGrid && this._viewer.querySelector('.product__media-item.swiper-slide-active')) ||
        this._viewer.querySelector('.product__media-item');
      return item?.querySelector('.product__media-container');
    }

    _syncFrame() {
      if (this._phase === 'done' || this._phase === 'leaving') return;
      const frame = this._getFrame();
      if (!frame) {
        this._dismiss(true);
        return;
      }
      if (frame !== this._frame) {
        if (this._phase === 'visible') {
          this._dismiss(true);
          return;
        }
        this._mediaCleanup?.();
        if (this._frame) {
          this._resizeObserver?.unobserve(this._frame);
          this._intersectionObserver?.unobserve(this._frame);
        }
        this._frame = frame;
        this._mediaReady = false;
        this._intersecting = false;
        this._hasBounds = false;
        this._resizeObserver?.observe(frame);
        this._intersectionObserver?.observe(frame);
        this._waitForMedia(frame);
      }
      this._scheduleMeasure();
    }

    _waitForMedia(frame) {
      const image = frame.querySelector('video-element > img, .product__media > img, img');
      const video = frame.querySelector('video');
      const media = image || video;
      let settled = false;
      const readyEvent = image ? 'load' : 'loadeddata';
      let fallback;
      const cleanup = () => {
        settled = true;
        clearTimeout(fallback);
        media?.removeEventListener(readyEvent, decode);
        media?.removeEventListener('error', ready);
      };
      const ready = () => {
        if (settled) return;
        cleanup();
        this._mediaCleanup = null;
        if (this._phase !== 'waiting' || this._frame !== frame) return;
        this._mediaReady = true;
        this._maybeShow();
      };
      const decode = () => {
        if (image && typeof image.decode === 'function') {
          try {
            Promise.resolve(image.decode()).catch(() => {}).then(ready);
          } catch (_) {
            ready();
          }
        } else ready();
      };
      this._mediaCleanup = cleanup;
      // A stalled image request must not leave a live observer indefinitely.
      fallback = setTimeout(ready, 4000);
      if (!media || (image && image.complete) || (!image && video.readyState >= 2)) decode();
      else {
        media.addEventListener(readyEvent, decode, { once: true });
        media.addEventListener('error', ready, { once: true });
      }
    }

    _scheduleMeasure() {
      if (this._measureFrame || this._phase === 'done') return;
      this._measureFrame = requestAnimationFrame(() => {
        this._measureFrame = null;
        this._measure();
      });
    }

    _measure() {
      if (!this._frame?.isConnected || !this._host?.isConnected) return;
      const frame = this._frame.getBoundingClientRect();
      const host = this._host.getBoundingClientRect();
      const scaleX = this._host.offsetWidth ? host.width / this._host.offsetWidth : 1;
      const scaleY = this._host.offsetHeight ? host.height / this._host.offsetHeight : 1;
      if (scaleX <= 0 || scaleY <= 0 || frame.width <= 0 || frame.height <= 0) return;
      this.style.left = `${(frame.left - host.left) / scaleX + this._host.scrollLeft - this._host.clientLeft}px`;
      this.style.top = `${(frame.top - host.top) / scaleY + this._host.scrollTop - this._host.clientTop}px`;
      this.style.width = `${frame.width / scaleX}px`;
      this.style.height = `${frame.height / scaleY}px`;
      this._hasBounds = true;
      if (!this._intersectionObserver) {
        const visibleWidth = Math.max(0, Math.min(frame.right, window.innerWidth) - Math.max(frame.left, 0));
        const visibleHeight = Math.max(0, Math.min(frame.bottom, window.innerHeight) - Math.max(frame.top, 0));
        this._intersecting = visibleWidth * visibleHeight / (frame.width * frame.height) >= .15;
      }
      this._maybeShow();
    }

    _maybeShow() {
      if (this._phase !== 'waiting' || !this.isConnected || document.hidden ||
          !this._mediaReady || !this._intersecting || !this._hasBounds) return;
      this._phase = 'visible';
      this._gallery.setAttribute('data-ll-cue-active', '');
      this.hidden = false;
      this.setAttribute('data-state', 'visible');
      this._holdTimer = setTimeout(() => this._dismiss(false), 2200);
    }

    _dismiss(immediate) {
      if (this._phase === 'done') return;
      clearTimeout(this._holdTimer);
      if (immediate || this._phase !== 'visible' ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this._complete();
        return;
      }
      this._phase = 'leaving';
      this.setAttribute('data-state', 'leaving');
      this._hideTimer = setTimeout(() => this._complete(), 300);
    }

    _complete() {
      this._phase = 'done';
      this.hidden = true;
      this.removeAttribute('data-state');
      this._gallery?.removeAttribute('data-ll-cue-active');
      clearTimeout(this._holdTimer);
      clearTimeout(this._hideTimer);
      cancelAnimationFrame(this._measureFrame);
      this._mediaCleanup?.();
      this._mediaCleanup = null;
      this._events?.abort();
      this._resizeObserver?.disconnect();
      this._intersectionObserver?.disconnect();
      this._mutationObserver?.disconnect();
    }
  });
})();
